import styled from '@emotion/styled'
import * as React from 'react'
import { Mutation } from 'react-apollo'

import { LOCIZE_NAMESPACE } from 'main/common'
import { ChildFields } from 'main/components/common/AuthModal/common'
import { i18nKeyPrefix } from 'main/components/lead/i18n'
import { Button, palette, spacing, Strong, Text, TextArea, TextField } from 'main/ui'
import { formatAddress } from 'main/util/address'
import { dateInputIsValid, formatDateInput } from 'main/util/date'
import { formatPhoneE164 } from 'main/util/phone'
import { TFunction, useTranslation } from 'react-i18next'
import {
  CONTACT_SCHOOL_MODAL_QUERY,
  ContactSchoolModal_school,
  LEAD_CONTACT_MUTATION,
  LeadContact,
  LeadContactMutationFn,
  LeadContactVariables,
  REQUEST_INFO_MODAL_QUERY,
  RequestInfoModal_externalSchool,
} from '../../gql'

import { AuthenticatedUser, SchoolContainer, SchoolType } from '../../types'
import { getDirectorFirstName, getSchoolName } from '../../util'

interface IProps {
  user: AuthenticatedUser
  schoolContainer: SchoolContainer
  onComplete: (callbackData?: {}) => void
}

interface IChildParams {
  birthday: string
  desiredEnrollmentDate: string
}

interface IParams {
  note: string
  password: string
  childParams: IChildParams
}

interface IValidation {
  note?: string
  password?: string
  childParams?: {
    birthday?: string
    desiredEnrollmentDate?: string
  }
}

interface IWithSchool {
  school: ContactSchoolModal_school
}

interface IWithExternalSchool {
  externalSchool: RequestInfoModal_externalSchool
}

const StyledText = styled(Text)`
  color: ${palette.slate};
`
const PRE = `${i18nKeyPrefix}-CONTACT-MODAL-STEP-CONTACT`

export default function Contact(props: IProps) {
  const { user, schoolContainer, onComplete } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const collectPassword = !user.hasPassword
  const collectchildParams = user.family.children.length === 0

  const [params, setParams] = React.useState<IParams>({
    childParams: { birthday: '', desiredEnrollmentDate: '' },
    note: t(`${PRE}-DEFAULT-NOTE`),
    password: '',
  })
  const [validationHints, setValidationHints] = React.useState<IValidation>({})

  return (
    <div>
      <div css={{ marginBottom: spacing.majorScale(2) }}>
        <Strong>{t(getSubtext(schoolContainer))}</Strong>
      </div>
      {renderChildMaybe()}
      <TextArea
        placeholder={t(getMessageInputPlaceholder(schoolContainer))}
        validationHint={validationHints.note}
        value={params.note}
        onChange={note => handleChange({ note }, setParams)}
      />
      <div css={{ marginTop: spacing.majorScale(2) }}>
        <StyledText>{getFinePrint(schoolContainer, t)}</StyledText>
      </div>
      {renderPasswordMaybe()}

      {schoolContainer.__type === SchoolType.Internal && (
        <SendMessageButton school={schoolContainer.school} />
      )}
      {schoolContainer.__type === SchoolType.External && (
        <SendRequestButton externalSchool={schoolContainer.externalSchool} />
      )}
    </div>
  )

  function SendMessageButton({ school }: IWithSchool) {
    return (
      <Mutation<LeadContact, LeadContactVariables>
        mutation={LEAD_CONTACT_MUTATION}
        onCompleted={() => onComplete()}
        refetchQueries={[{ query: CONTACT_SCHOOL_MODAL_QUERY, variables: { schoolId: school.id } }]}
      >
        {(mutate, { loading }) => (
          <Button
            css={{ marginTop: spacing.majorScale(4) }}
            disabled={loading}
            onClick={() =>
              handleSubmit(
                { schoolId: school.id, note: params.note },
                params,
                collectPassword,
                collectchildParams,
                setValidationHints,
                mutate,
              )
            }
            full
          >
            {t(`${PRE}-SEND-MESSAGE`)}
          </Button>
        )}
      </Mutation>
    )
  }

  function SendRequestButton({ externalSchool }: IWithExternalSchool) {
    const handleOnCompleted = () => {
      onComplete({
        childBirthday: params.childParams.birthday,
        desiredEnrollmentDate: params.childParams.desiredEnrollmentDate,
        directorFirstName: externalSchool.directorFirstName,
        directorLastName: externalSchool.directorLastName,
        directorPhone: formatPhoneE164(externalSchool.directorPhone),
        directorEmail: externalSchool.directorEmail,
        externalSchoolId: externalSchool.id,
        externalSchoolLocation: formatAddress(externalSchool.address),
        message: params.note,
        parentEmail: user.email,
        parentFirstName: user.firstName,
        parentLastName: user.lastName,
        parentPhone: user.phone,
        parentUserId: user.id,
        programName: externalSchool.name,
      })
    }

    return (
      <Mutation<LeadContact, LeadContactVariables>
        mutation={LEAD_CONTACT_MUTATION}
        onCompleted={() => handleOnCompleted()}
        refetchQueries={[{ query: REQUEST_INFO_MODAL_QUERY, variables: { id: externalSchool.id } }]}
      >
        {(mutate, { loading }) => (
          <Button
            css={{ marginTop: spacing.majorScale(4) }}
            disabled={loading}
            onClick={() =>
              handleSubmit(
                { externalSchoolId: externalSchool.id, note: params.note },
                params,
                collectPassword,
                collectchildParams,
                setValidationHints,
                mutate,
              )
            }
            full
          >
            {t(`${PRE}-SEND-REQUEST`)}
          </Button>
        )}
      </Mutation>
    )
  }

  function renderChildMaybe() {
    if (!collectchildParams) return null

    const data = params.childParams

    return (
      <ChildFields
        data={data}
        index={0}
        showLabel={false}
        validationHints={validationHints.childParams || {}}
        onChange={changes => {
          const childParams = { ...data, ...changes }
          handleChange({ childParams }, setParams)
        }}
      />
    )
  }

  function renderPasswordMaybe() {
    if (!collectPassword) return null

    return (
      <div css={{ marginTop: spacing.majorScale(4) }}>
        <div css={{ marginBottom: spacing.majorScale(2) }}>
          <Strong>Create a login so you can quickly find programs you contacted</Strong>
        </div>
        <TextField
          type="password"
          label="Password"
          placeholder="Enter a password"
          value={params.password}
          validationHint={validationHints.password}
          onChange={password => handleChange({ password }, setParams)}
          required
        />
      </div>
    )
  }
}

function handleChange(
  changes: Partial<IParams>,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
) {
  setParams(params => ({ ...params, ...changes }))
}

function handleSubmit(
  variables: LeadContactVariables,
  params: IParams,
  collectPassword: boolean,
  collectchildParams: boolean,
  setValidationHints: React.Dispatch<React.SetStateAction<IValidation>>,
  mutate: LeadContactMutationFn,
) {
  const validationHints = validateParams(params, !collectPassword, !collectchildParams)

  if (Object.keys(validationHints).length > 0) {
    return setValidationHints(validationHints)
  }

  variables = collectPassword ? { ...variables, password: params.password } : variables
  variables = collectchildParams
    ? { ...variables, childParams: formatChildParamsDates(params.childParams) }
    : variables

  return mutate({ variables })
}

function validateParams(params: IParams, skipPassword: boolean, skipchildParams: boolean) {
  return [
    validateNote(params.note),
    skipPassword ? {} : validatePassword(params.password),
    skipchildParams ? {} : validatechildParams(params.childParams),
  ].reduce((acc, x) => ({ ...acc, ...x }), {})
}

function validateNote(note: string) {
  return note.trim() === '' ? { note: 'Please enter a message' } : {}
}

function validatePassword(password: string) {
  return password === '' ? { password: 'Please enter a password' } : {}
}

function validatechildParams(childParams: IChildParams) {
  const childParamsValidationHints = [
    validateBirthday(childParams.birthday),
    validateDesiredEnrollmentDate(childParams.desiredEnrollmentDate),
  ].reduce((acc, x) => ({ ...acc, ...x }), {})

  return Object.keys(childParamsValidationHints).length > 0
    ? { childParams: childParamsValidationHints }
    : {}
}

function validateBirthday(birthday: string) {
  if (birthday === '') {
    return { birthday: "Please enter your child's date of birth" }
  }

  if (!dateInputIsValid(birthday)) {
    return { birthday: 'Please enter a valid date (mm/dd/yyyy)' }
  }

  return {}
}

function validateDesiredEnrollmentDate(desiredEnrollmentDate: string) {
  if (desiredEnrollmentDate === '') {
    return { desiredEnrollmentDate: 'Please enter your desired start date' }
  }

  if (!dateInputIsValid(desiredEnrollmentDate)) {
    return { desiredEnrollmentDate: 'Please enter a valid date (mm/dd/yyyy)' }
  }

  return {}
}

function formatChildParamsDates(childParams: IChildParams) {
  return {
    ...childParams,
    birthday: formatDateInput(childParams.birthday),
    desiredEnrollmentDate: formatDateInput(childParams.desiredEnrollmentDate),
  }
}

function getFinePrint(schoolContainer: SchoolContainer, t: TFunction) {
  switch (schoolContainer.__type) {
    case SchoolType.Internal:
      const schoolName = getSchoolName(schoolContainer)
      const directorFirstName = getDirectorFirstName(schoolContainer)
      if (directorFirstName) {
        return t(`${PRE}-FINE-PRINT-DIRECTOR`, { schoolName, directorFirstName })
      }
      return t(`${PRE}-FINE-PRINT`, { schoolName })

    case SchoolType.External:
      return t(`${PRE}-FINE-PRINT-EXTERNAL`)
  }
}

function getSubtext(schoolContainer: SchoolContainer) {
  switch (schoolContainer.__type) {
    case SchoolType.Internal:
      return `${PRE}-SUBTEXT-INTERNAL`
    case SchoolType.External:
      return `${PRE}-SUBTEXT-EXTERNAL`
  }
}

function getMessageInputPlaceholder(schoolContainer: SchoolContainer) {
  switch (schoolContainer.__type) {
    case SchoolType.Internal:
      return `${PRE}-INPUT-INTERNAL`
    case SchoolType.External:
      return `${PRE}-INPUT-EXTERNAL`
  }
}
